import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import ShiftLeaderIcon from '@mui/icons-material/AccountCircle';
import PioneerIcon from '@mui/icons-material/PermIdentityOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import InformationalSection from '~/components/InformationalSection';
import PublisherCard from '~/components/PublisherCard';
import PublisherExperienceTag from '../../components/PublisherExperienceTag';
import messages from './messages';

export const getPublisherName = (publisher: {
  firstName: string;
  middleName?: string;
  lastName: string;
}) => [publisher.firstName, publisher.middleName, publisher.lastName].join(' ');

type Publisher = {
  email: string;
  firstName: string;
  middleName?: string;
  id: number;
  languages: string[];
  lastName: string;
  phone: string;
  experienceLevel: string;
  hasVehicle: boolean;
  gender?: string;
};

type AssignmentListCardProps = {
  shiftLeader: Publisher;
  assignedPublishers: Array<Publisher>;
  userIsShiftLeader: boolean;
};

export const AssignmentListCard = (props: AssignmentListCardProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const copyPublisherNamesToClipboard = async () => {
    await navigator.clipboard.writeText(
      [props.shiftLeader, ...props.assignedPublishers]
        .map((publisher) => getPublisherName(publisher))
        .join('\n')
    );
    showSnackbar({
      message: intl.formatMessage(messages.copiedPublisherNamesSuccessfully),
    });
  };
  return (
    <InformationalSection
      title={
        <span>
          <FormattedMessage {...messages.assignedPublishers} />
          {` (${1 + props.assignedPublishers.length})`}
        </span>
      }
    >
      <List disablePadding>
        <PublisherCard key={props.shiftLeader.id} publisher={props.shiftLeader}>
          {(args) => (
            <ListItem disablePadding>
              <ListItemButton
                onClick={args.onClick}
                aria-describedby={args['aria-describedby']}
                component="button"
              >
                <ListItemIcon>
                  <ShiftLeaderIcon />
                </ListItemIcon>
                <ListItemText
                  primary={getPublisherName(props.shiftLeader)}
                  secondary={
                    <>
                      <FormattedMessage {...messages.shiftLeader} />
                      {props.shiftLeader.hasVehicle ? (
                        <>
                          {' · '}
                          <FormattedMessage {...messages.appliedWithVehicle} />
                        </>
                      ) : null}
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          )}
        </PublisherCard>
        {props.assignedPublishers
          .sort((publisherA, publisherB) =>
            publisherA.lastName > publisherB.lastName ? 1 : -1
          )
          .map((publisher) => (
            <PublisherCard key={publisher.id} publisher={publisher}>
              {(args) => (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={args.onClick}
                    aria-describedby={args['aria-describedby']}
                    component="button"
                  >
                    <ListItemIcon>
                      <PioneerIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={getPublisherName(publisher)}
                      secondary={
                        publisher.hasVehicle ? (
                          <FormattedMessage {...messages.appliedWithVehicle} />
                        ) : null
                      }
                    />
                    <PublisherExperienceTag
                      experienceLevel={
                        publisher.experienceLevel as
                          | 'Newbie'
                          | 'Common'
                          | 'Mentor'
                      }
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </PublisherCard>
          ))}
      </List>
      {props.userIsShiftLeader ? (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Button variant="outlined" onClick={copyPublisherNamesToClipboard}>
            Alle Namen kopieren
          </Button>
        </Box>
      ) : null}
    </InformationalSection>
  );
};

export default AssignmentListCard;
